import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, RotateCcw } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api, { errorMessage } from "../services/api";
import { describeCancellation, formatTripDateTime, parseBookingIdInput, toDisplayValue } from "../utils/tripLog";

// Per-trip support view.
//
// Answers "what actually happened on this ride" from evidence instead of from
// whoever is on the phone. Three things, in one place:
//   • an ordered timeline of every state change we persist a timestamp for;
//   • which drivers were offered the ride and how each responded — this is what
//     explains an expired request;
//   • every push sent, and whether the device confirmed receiving it.

const KIND_STYLES = {
  booking: "bg-blue-100 text-blue-800",
  offer: "bg-purple-100 text-purple-800",
  ride: "bg-green-100 text-green-800",
  push: "bg-amber-100 text-amber-800",
  money: "bg-emerald-100 text-emerald-800",
};

const OFFER_STYLES = {
  ACCEPTED: "text-green-600",
  NOTIFIED: "text-gray-500",
  EXPIRED: "text-red-600",
  DECLINED: "text-red-600",
  IGNORED: "text-red-600",
};

// Who cancelled and why, worded the same as Trip Logs and the ride timeline.
const CancellationBox = ({ booking }) => {
  const cancellation = describeCancellation(booking);
  if (!cancellation) return null;
  return (
    <div className="mt-4 rounded-xl bg-rose-50 p-3 dark:bg-rose-500/10">
      <p className="text-xs uppercase tracking-wide text-gray-400">Cancellation</p>
      <p className="text-sm text-gray-900 dark:text-white">{cancellation.text}</p>
      {cancellation.at ? (
        <p className="text-xs text-gray-500">{formatTripDateTime(cancellation.at)}</p>
      ) : null}
    </div>
  );
};

const Field = (props) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400">{props.label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
      {props.value}
    </p>
  </div>
);

const TripInspector = () => {
  const { bookingId: routeBookingId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(routeBookingId ?? "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  const load = useCallback(async (id) => {
    const cleaned = parseBookingIdInput(id);
    if (!cleaned) {
      setData(null);
      setLookupError("That isn't a booking ID. Paste an ID like #BCE449B6 or a trip inspector link.");
      return;
    }
    setLoading(true);
    setLookupError(null);
    try {
      const response = await api.get(`/v1/admin/bookings/${encodeURIComponent(cleaned)}/inspect`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to inspect trip:", error);
      setData(null);
      // The server's 404/400 text names the problem ("No ride found with ID #…").
      setLookupError(errorMessage(error, "Couldn't load this ride. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (routeBookingId) load(routeBookingId);
  }, [routeBookingId, load]);

  const onSubmit = (event) => {
    event.preventDefault();
    const cleaned = parseBookingIdInput(query);
    if (!cleaned) {
      setData(null);
      setLookupError(
        query.trim() ? "That isn't a booking ID. Paste an ID like #BCE449B6 or a trip inspector link." : "Enter a booking ID.",
      );
      return;
    }
    setQuery(cleaned);
    // Drive off the URL so a support conversation can be shared as a link.
    if (cleaned === routeBookingId) load(cleaned);
    else navigate(`/trip-inspector/${cleaned}`);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex w-full flex-1 flex-col overflow-hidden lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg border border-gray-300 p-2 text-gray-600 dark:border-gray-600 dark:text-gray-300"
              title="Back"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Trip inspector
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enter a booking ID to see the full ordered history.
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="mb-6 max-w-xl">
            <div className="flex gap-2">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Booking ID, e.g. #BCE449B6"
                aria-label="Booking ID"
                aria-invalid={Boolean(lookupError)}
                aria-describedby={lookupError ? "inspect-error" : undefined}
                className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100 ${
                  lookupError ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-600"
                }`}
              />
              <button
                type="submit"
                className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white dark:bg-white dark:text-gray-900"
              >
                <Search size={16} /> Inspect
              </button>
              {data && (
                <button
                  type="button"
                  onClick={() => load(data.booking.id)}
                  className="rounded-lg border border-gray-300 p-2 text-gray-600 dark:border-gray-600 dark:text-gray-300"
                  title="Refresh"
                >
                  <RotateCcw size={16} />
                </button>
              )}
            </div>
            {lookupError && (
              <p id="inspect-error" role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
                {lookupError}
              </p>
            )}
          </form>

          {loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          )}

          {!loading && data && (
            <div className="space-y-6">
              <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <Field label="Status" value={data.booking.status} />
                  <Field label="Seats" value={data.booking.seats ?? "—"} />
                  <Field
                    label="Route"
                    value={`${toDisplayValue(data.booking.pickupLocationName)} → ${toDisplayValue(data.booking.dropoffLocationName)}`}
                  />
                  <Field
                    label="Requested"
                    value={formatTripDateTime(data.booking.createdAt)}
                  />
                  <Field
                    label="Passenger"
                    value={toDisplayValue(data.passenger?.name)}
                  />
                  <Field
                    label="Passenger phone"
                    value={toDisplayValue(data.passenger?.phone)}
                  />
                  <Field
                    label="Rider"
                    value={toDisplayValue(data.driver?.name)}
                  />
                  <Field
                    label="Pickup code"
                    value={toDisplayValue(data.booking.securityCode)}
                  />
                </div>
                <CancellationBox booking={data.booking} />
              </section>

              <section>
                <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                  Timeline
                </h2>
                <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                  {data.timeline.length === 0 && (
                    <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                      No timestamped events.
                    </p>
                  )}
                  <ol className="divide-y divide-gray-100 dark:divide-gray-700">
                    {data.timeline.map((event, index) => (
                      <li
                        key={`${event.at}-${index}`}
                        className="flex flex-wrap items-baseline gap-3 p-4"
                      >
                        <span className="w-40 shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400">
                          {formatTripDateTime(event.at)}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${KIND_STYLES[event.kind] ?? "bg-gray-100 text-gray-700"}`}
                        >
                          {event.kind}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {event.label}
                        </span>
                        {event.detail && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {event.detail}
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              </section>

              <section>
                <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                  Drivers offered ({data.offers.length})
                </h2>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  If a request expired, this is why — either nobody was offered
                  it, or everyone offered it let it lapse.
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Rider</th>
                        <th className="px-4 py-3 font-medium">Offered</th>
                        <th className="px-4 py-3 font-medium">Responded</th>
                        <th className="px-4 py-3 font-medium">Outcome</th>
                        <th className="px-4 py-3 font-medium">On campus</th>
                        <th className="px-4 py-3 font-medium">Nudges</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {data.offers.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-4 text-gray-500 dark:text-gray-400"
                          >
                            No drivers were offered this ride.
                          </td>
                        </tr>
                      )}
                      {data.offers.map((offer, index) => (
                        <tr
                          key={`${offer.driverName}-${index}`}
                          className="text-gray-700 dark:text-gray-200"
                        >
                          <td className="px-4 py-3">{offer.driverName}</td>
                          <td className="px-4 py-3">
                            {formatTripDateTime(offer.notifiedAt)}
                          </td>
                          <td className="px-4 py-3">
                            {offer.respondedAt
                              ? formatTripDateTime(offer.respondedAt)
                              : "—"}
                          </td>
                          <td
                            className={`px-4 py-3 font-medium ${OFFER_STYLES[offer.status] ?? ""}`}
                          >
                            {offer.status}
                          </td>
                          <td className="px-4 py-3">
                            {offer.wasInUnilorin === false ? "No" : "Yes"}
                          </td>
                          <td className="px-4 py-3">{offer.nudgeCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                  Notifications ({data.notifications.length})
                </h2>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  &ldquo;Confirmed&rdquo; means the device itself reported
                  surfacing the push. Sent-but-never-confirmed on a specific
                  handset is the signature of an OEM battery manager or a muted
                  channel.
                </p>
                <div className="space-y-3">
                  {data.notifications.length === 0 && (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                      No notifications recorded for this booking.
                    </div>
                  )}
                  {data.notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {notification.title}
                        </p>
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                          {formatTripDateTime(notification.createdAt)}
                        </span>
                      </div>
                      {notification.message && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {notification.message}
                        </p>
                      )}
                      {notification.deliveries.length === 0 ? (
                        <p className="mt-2 text-xs text-amber-600">
                          No send recorded — the user had no registered device,
                          or this predates delivery tracking.
                        </p>
                      ) : (
                        <ul className="mt-2 space-y-1">
                          {notification.deliveries.map((delivery, index) => (
                            <li
                              key={`${delivery.deviceId}-${index}`}
                              className="flex flex-wrap items-center gap-2 text-xs"
                            >
                              <span className="text-gray-700 dark:text-gray-200">
                                {toDisplayValue(delivery.deviceModel)} (
                                {delivery.platform})
                              </span>
                              <span className="text-gray-400">·</span>
                              <span
                                className={
                                  delivery.sendOk
                                    ? "text-gray-500 dark:text-gray-400"
                                    : "text-red-600"
                                }
                              >
                                {delivery.sendOk
                                  ? "sent"
                                  : "provider rejected send"}
                              </span>
                              <span className="text-gray-400">·</span>
                              <span
                                className={
                                  delivery.acknowledgedAt
                                    ? "font-medium text-green-600"
                                    : "font-medium text-red-600"
                                }
                              >
                                {delivery.acknowledgedAt
                                  ? `confirmed ${formatTripDateTime(delivery.acknowledgedAt)}`
                                  : "never confirmed"}
                              </span>
                              {delivery.channelId && (
                                <>
                                  <span className="text-gray-400">·</span>
                                  <span className="text-gray-500 dark:text-gray-400">
                                    {delivery.channelId}
                                  </span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Socket connection state is not shown because it is not persisted
                anywhere yet — this view reflects database state and push
                telemetry only.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TripInspector;
